/**
 * Marcador de campanha no clique do WhatsApp.
 *
 * O link `wa.me` não carrega nenhuma informação de origem por padrão. Para o
 * ConectAI saber de qual campanha veio o lead, anexamos um marcador curto ao
 * texto pré-preenchido: `Olá! ... [c:medicina-integrativa|g:<gclid>]`. O
 * webhook do ConectAI lê o marcador, grava a campanha na conversa e remove o
 * código do texto — a equipe e a IA veem a mensagem limpa.
 *
 * Contrato do marcador (espelhado em `extractSiteMarker` no ConectAI; a
 * especificação completa vive lá, em `docs/MARCADOR-SITE-FORMATO.md`):
 *   [c:<utm_campaign>|f:<fbclid>|g:<gclid>|p:<fbp>|s:<utm_source>|m:<utm_medium>]
 *   ordem fixa c, f, g, p, s, m · tudo depois de `c:` é opcional e omitido quando
 *   não existe · campanha/source/medium até 80 chars · ids até 255 chars válidos.
 *   `c:-` = visita sem `utm_campaign` (os demais campos continuam valendo).
 *
 * `s:` e `m:` entraram em 02/11/2026 (Fase 2 do rastreamento de origem,
 * ADR-018 do ConectAI): são o que separa anúncio pago de link na bio ou Google
 * orgânico na classificação de origem. Na mesma versão o `c:-` substituiu os
 * rótulos `google-ads`/`meta-ads` que este módulo inventava quando só havia
 * click id — `fbclid` vem em QUALQUER link clicado dentro do Facebook ou do
 * Instagram (bio, post orgânico), então `meta-ads` rotulava orgânico como pago.
 *
 * Este módulo existe porque a versão do Google Tag Manager chegava tarde
 * demais: o GTM do site é carregado com `load` + `setTimeout(2000)`, então a
 * tag só passava a marcar ~3,2 s depois da navegação, enquanto a página fica
 * clicável em ~0,16 s. Quem clicava no CTA logo que chegava — o normal numa
 * landing page de anúncio — não era marcado. Rodando junto do bundle, a
 * captura acontece no primeiro instante.
 *
 * Este é o ÚNICO lugar que marca campanha. A tag HTML equivalente do GTM
 * ("ConectAI - Rastreio WhatsApp") foi removida em 01/11/2026 (versão
 * "Fase 0 — rastreamento", ver docs/decisoes.md): ela só cobria
 * `utm_campaign` e `gclid`, ignorava `NUMEROS_SEM_CONECTAI` e era uma segunda
 * fonte de verdade para o mesmo contrato. Se este módulo deixar de rodar,
 * ninguém mais marca — não existe rede de segurança no GTM.
 *
 * A guarda `__caiMarker` continua: evita listener duplicado se o módulo for
 * inicializado duas vezes (HMR, import repetido). O `if (texto.includes('[c:'))`
 * em `comMarcador()` também fica, porque um link já marcado não pode ganhar um
 * segundo marcador.
 */

import { temConsentimentoPublicidade } from './consentimento';

const CHAVE_CAMPANHA = 'cai_utm_campaign';
const CHAVE_SOURCE = 'cai_utm_source';
const CHAVE_MEDIUM = 'cai_utm_medium';
const CHAVE_FBCLID = 'cai_fbclid';
const CHAVE_GCLID = 'cai_gclid';

/** `c:-`: visita sem `utm_campaign` — o parser grava campanha nula. */
const SEM_CAMPANHA = '-';

/**
 * O `fbclid` engorda o texto que o visitante vê antes de enviar, mas é o único
 * dado que amarra a conversa ao clique exato do anúncio — sem ele a Meta
 * recebe o evento de conversão e não consegue atribuí-lo a campanha nenhuma.
 *
 * Ligado em 30/07/2026: há campanha da Meta apontando pro site (as conversas
 * `LEADSFRIOWPP - … via LP` chegavam com `utm_campaign` e sem click id), e a
 * medição no banco mostrou `fbclid` preenchido em ZERO das 2.214 conversas —
 * ou seja, o `fbc` nunca tinha sido enviado à API de Conversões.
 */
const INCLUIR_FBCLID = true;

const HOSTS_WHATSAPP = ['wa.me', 'api.whatsapp.com', 'whatsapp.com'];

/**
 * Números de WhatsApp que NÃO estão conectados ao ConectAI.
 *
 * O marcador só faz sentido quando há um ConectAI do outro lado para ler e
 * apagar o código. Num número que atende pelo aplicativo comum a mensagem chega
 * crua — foi o caso da /drclaudiomessias em 06/09/2026: a paciente mandou
 * "…quero avaliar blefaroplastia. [c:cirurgia-plastica|g:EAIaIQ…|p:fb.2.…]" e a
 * equipe do Dr. Claudio viu tudo isso na tela. Para esses números o link sai
 * limpo. Custo: sem `gclid` na conversa, não há upload de conversão offline no
 * Google Ads para eles; a conversão de clique via GTM continua contando.
 *
 * Se o número for conectado ao ConectAI, basta tirá-lo daqui.
 *
 * Vazia desde 27/10/2026: o número do Dr. Claudio saiu junto com a página dele.
 * A lista fica porque a regra continua valendo para qualquer número novo que
 * atenda fora do ConectAI.
 */
const NUMEROS_SEM_CONECTAI: string[] = [];

/**
 * Charset que o parser do ConectAI aceita nos ids (`extractSiteMarker`, nos dois
 * webhooks). Um id com caractere fora dele faz a regex de lá falhar no marcador
 * INTEIRO — perderíamos também a campanha. Melhor mandar sem o id.
 */
const ID_VALIDO = /^[A-Za-z0-9._-]{1,255}$/;

/**
 * Campos de texto livre (`c:`, `s:`, `m:`): o parser aceita qualquer coisa
 * menos `|` e `]`, até 80 chars. Sanitizar (em vez de descartar, como nos ids)
 * é seguro aqui porque o valor mutilado continua legível no relatório.
 */
function sanitizarUtm(valor: string | null, max: number): string | null {
  if (!valor) return null;
  const limpo = valor.replace(/[^\w\-. ]/g, '').trim().slice(0, max);
  return limpo || null;
}

declare global {
  interface Window {
    __caiMarker?: boolean;
  }
}

function ler(chave: string): string | null {
  try {
    return sessionStorage.getItem(chave);
  } catch {
    return null;
  }
}

function guardar(chave: string, valor: string | null): void {
  if (!valor) return;
  try {
    sessionStorage.setItem(chave, valor);
  } catch {
    /* modo privado / storage bloqueado: segue sem marcar */
  }
}

/**
 * Lê o `_fbp`, cookie que o pixel da Meta cria no primeiro carregamento.
 *
 * É cookie, não parâmetro de URL — por isso é lido no CLIQUE e não na chegada:
 * o pixel pode criá-lo depois do boot do bundle, e ler cedo demais devolveria
 * vazio. Vale para toda a janela de atribuição (o cookie dura 90 dias),
 * inclusive em visita orgânica de quem já tinha visto o anúncio antes.
 *
 * Junto do `fbc`, é dos parâmetros de maior peso na Event Match Quality da API
 * de Conversões — a da Habitus estava em 4,6/10 em 30/07/2026.
 */
function lerFbp(): string | null {
  try {
    const achado = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/);
    return achado ? validarId(decodeURIComponent(achado[1])) : null;
  } catch {
    return null;
  }
}

/** Devolve o id se ele passa no charset do parser; senão `null` (descarta). */
function validarId(valor: string | null): string | null {
  return valor && ID_VALIDO.test(valor) ? valor : null;
}

/** É um link que abre uma conversa no WhatsApp? */
function ehLinkWhatsApp(url: URL): boolean {
  return HOSTS_WHATSAPP.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`));
}

/**
 * Número de destino do link, só dígitos: `?phone=5561…` (api.whatsapp.com e
 * whatsapp.com/send) ou o primeiro segmento do caminho (`wa.me/5561…`).
 */
function numeroDestino(url: URL): string | null {
  const porParametro = url.searchParams.get('phone');
  if (porParametro) return porParametro.replace(/\D/g, '') || null;
  const porCaminho = url.pathname.split('/').filter(Boolean)[0] || '';
  const digitos = porCaminho.replace(/\D/g, '');
  return digitos.length >= 10 ? digitos : null;
}

/** O destino atende fora do ConectAI? Aí o marcador só sujaria a mensagem. */
function destinoSemConectAI(url: URL): boolean {
  const numero = numeroDestino(url);
  return numero !== null && NUMEROS_SEM_CONECTAI.includes(numero);
}

/**
 * Monta ` [c:campanha|f:fbclid|g:gclid|p:fbp|s:source|m:medium]` do que a
 * visita tem disponível, na ordem fixa que o parser exige.
 */
function montarMarcador(): string | null {
  const campanha = sanitizarUtm(ler(CHAVE_CAMPANHA), 50);
  const source = sanitizarUtm(ler(CHAVE_SOURCE), 80);
  const medium = sanitizarUtm(ler(CHAVE_MEDIUM), 80);
  // id fora do charset derruba a regex do parser no marcador INTEIRO: validar
  // e descartar, nunca sanitizar (id mutilado é pior que nenhum).
  const gclid = validarId(ler(CHAVE_GCLID));
  const fbclid = INCLUIR_FBCLID ? validarId(ler(CHAVE_FBCLID)) : null;

  // Sem `utm_campaign` ainda vale marcar se houver qualquer outro sinal de
  // origem: o `gclid` é o que permite ao Google Ads atribuir a conversão, e
  // `utm_source`/`utm_medium` são o que separa pago de orgânico no ConectAI.
  // Nesse caso vai `c:-` e o parser grava campanha nula — não inventamos mais
  // rótulo de plataforma, porque `fbclid` sozinho não prova que foi anúncio.
  const temOutroSinal = Boolean(gclid || fbclid || source || medium);
  if (!campanha && !temOutroSinal) return null; // visita direta/orgânica sem sinal

  let marcador = ` [c:${campanha ?? SEM_CAMPANHA}`;
  if (fbclid) marcador += `|f:${fbclid}`;
  if (gclid) marcador += `|g:${gclid}`;
  // fbp entra só como enriquecimento de um clique JÁ marcado: sozinho ele não
  // é sinal de origem (todo visitante com o pixel tem um), e marcar visita
  // orgânica encheria os relatórios de campanha falsa.
  // E só com aceite de publicidade (LGPD, 03/11/2026): o `_fbp` é o cookie do
  // pixel do Meta. Sem aceite o pixel não deveria nem ter rodado, mas um
  // cookie que sobrou de antes do banner não pode viajar na mensagem. O resto
  // do marcador (campanha, origem, click ids) segue com ou sem consentimento.
  const fbp = temConsentimentoPublicidade() ? lerFbp() : null;
  if (fbp) marcador += `|p:${fbp}`;
  if (source) marcador += `|s:${source}`;
  if (medium) marcador += `|m:${medium}`;
  return `${marcador}]`;
}

/**
 * Devolve a URL com o marcador de campanha anexado ao texto pré-preenchido.
 *
 * URL que não é do WhatsApp volta intacta — é o que permite usar esta função
 * no caminho genérico de navegação do site (que também abre Google Maps).
 * Sem campanha na sessão, com o marcador já presente, ou com destino em
 * `NUMEROS_SEM_CONECTAI`, também volta intacta.
 */
export function comMarcador(url: string): string {
  try {
    const alvo = new URL(url, window.location.href);
    if (!ehLinkWhatsApp(alvo)) return url;
    if (destinoSemConectAI(alvo)) return url;

    const texto = alvo.searchParams.get('text') || 'Olá!';
    if (texto.includes('[c:')) return url;

    const marcador = montarMarcador();
    if (!marcador) return url;

    alvo.searchParams.set('text', texto + marcador);
    return alvo.toString();
  } catch {
    return url;
  }
}

/**
 * Captura a origem da visita e passa a marcar os cliques de WhatsApp.
 *
 * Chamar uma única vez, o mais cedo possível no boot. Faz duas coisas:
 *  1. guarda `utm_campaign`, `utm_source`, `utm_medium`, `gclid` e `fbclid`
 *     da URL de entrada na sessão — vale para a navegação inteira, inclusive
 *     depois de trocar de rota;
 *  2. instala um listener delegado que marca links `<a>` de WhatsApp que
 *     navegam de forma nativa (sem passar pelo `window.open` do site).
 */
export function iniciarMarcadorCampanha(): void {
  if (typeof window === 'undefined' || window.__caiMarker) return;
  window.__caiMarker = true;

  try {
    const params = new URLSearchParams(window.location.search);
    guardar(CHAVE_CAMPANHA, params.get('utm_campaign'));
    guardar(CHAVE_SOURCE, params.get('utm_source'));
    guardar(CHAVE_MEDIUM, params.get('utm_medium'));
    guardar(CHAVE_FBCLID, params.get('fbclid'));
    guardar(CHAVE_GCLID, params.get('gclid'));
  } catch {
    /* URL malformada: segue sem capturar */
  }

  document.addEventListener(
    'click',
    (e) => {
      const alvo = e.target as HTMLElement | null;
      const link = alvo?.closest?.(
        'a[href*="wa.me"], a[href*="api.whatsapp.com"], a[href*="whatsapp.com/send"]',
      ) as HTMLAnchorElement | null;
      if (!link) return;

      const comMarca = comMarcador(link.href);
      if (comMarca !== link.href) link.href = comMarca;
    },
    true, // capture: roda antes de qualquer handler do React
  );
}
